import { Component, ElementRef, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { FormType, ResourceType } from 'src/app/common/enumerations/enumerations';
import { Resource, ResourceRelation } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { RestrictionsRefresh } from 'src/app/components/scf-v2/store/restrictions/restrictions.action';
import { Restriction } from 'src/app/controls/restrictions/services/restriction';
import { FormRestriction } from 'src/app/services/forms/form-restriction';

@Component({
  selector: 'restriction-available-autocomplete',
  templateUrl: './restriction-available-autocomplete.component.html',
  styleUrls: ['./restriction-available-autocomplete.component.scss']
})
export class RestrictionAvailableAutocompleteComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formType!: FormType;
  @Input() placeholder?: string;
  @Input() disabled?: boolean | null;
  @Input() formRestrictions?: FormRestriction[];
  @Input() locationIDs?: (number | undefined)[];
  @Input() control: FormControl = new FormControl(null);
  @Input() locationAddedIDs?: number[];

  @Output() onError = new EventEmitter<string>();
  @Output() onRestrictionSelected = new EventEmitter<Restriction>();
  @Output() onFreeTextRestrictionWritten = new EventEmitter<string>();

  @ViewChild('restrictionInput') restrictionInput?: ElementRef<HTMLInputElement>;

  allRestrictions?: Restriction[];
  availableRestrictions?: Restriction[];
  filteredRestrictions?: Restriction[];
  uniqueRestrictions?: Restriction[];

  resources: Resource[] = [];
  resourcesFiltered?: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;
  resourceRelations: ResourceRelation[] = [];

  restrictions: Restriction[] = [];
  restrictionsFiltered?: Restriction[];
  restrictions$!: Observable<Restriction[]>;
  restrictionsSubs!: Subscription;

  noSubscript?: boolean;
  private pendingAutoRestrictionIDs: number[] = [];
  private restrictionsRefreshRequested = false;

  constructor(
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.clear();
    this.loadResources();
    const valid = this.control?.valid;
    if (this.disabled)
      this.control?.disable();
    else this.control?.enable();
    if (!valid)
      this.control?.setErrors({ required: true });
    if (this.locationAddedIDs?.length) {
      // Defer to a microtask: autoRestriction() emits selected restrictions up to
      // the form-restriction-table -> Section 2's formRestrictions. Running it
      // synchronously here mutates that bound array during the current
      // change-detection pass and throws NG0100. Capture + clear the ids first so
      // re-entrant ngOnChanges calls don't double-process.
      const ids = this.locationAddedIDs;
      this.locationAddedIDs = [];
      this.queueAutoRestrictions(ids);
    }
    setTimeout(() => {
      this.noSubscript = this.control.valid;
    }, 100);
  }

  ngOnInit(): void {

  }

  override ngOnDestroy(): void {
    this.resourcesSubs?.unsubscribe();
    this.restrictionsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  // trackBy so the rebuilt option list reuses DOM instead of full re-render.
  trackByRestrictionId = (_: number, r: any) => r?.id;

  /**
   * Loads resources before restriction lookup and clears prior subscriptions from repeated input changes.
   *
   * This method hydrates parent relations first because Beamline restrictions can be inherited from
   * parent shutter or accelerator resources.
   */
  loadResources() {
    this.resourcesSubs?.unsubscribe();
    this.restrictionsSubs?.unsubscribe();
    this.resources$ = this.store.select((state) => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe((data) => {
      if (data.length) {
        this.hydrateResources(data);
        this.loadRestrictions();
      }
    });
  }

  /**
   * Rebuilds the local resource cache with parent relations from the store relation graph.
   * @param data - Resources emitted by the store.
   */
  private hydrateResources(data: Resource[]): void {
    const snapshotResources = this.utils.cloneDeep(
      this.store.selectSnapshot((state: any) => state.Resources?.data ?? [])
    ) as Resource[];
    const resources = this.utils.cloneDeep((data?.length ? data : snapshotResources) ?? []);
    const relations = this.utils.cloneDeep(
      this.store.selectSnapshot((state: any) => state.ResourceRelations?.data ?? [])
    ) as ResourceRelation[];
    const resourcesById = new Map(resources.map(resource => [resource.id, resource]));

    resources.forEach(resource => {
      const parentRelations = relations.filter(relation => relation.childResourceID === resource.id);

      resource.parentResources = (parentRelations.length ? parentRelations : resource.parentResources ?? []).map(relation => ({
        ...relation,
        parentResource: relation.parentResource ?? resourcesById.get(relation.parentResourceID ?? 0) ?? null
      })) ?? [];
    });

    this.resources = resources;
    this.resourceRelations = relations;
  }

  /**
   * Stores added locations until Resources and Restrictions are both ready.
   * @param ids - Location IDs that should auto-add missing restrictions.
   */
  private queueAutoRestrictions(ids: number[]): void {
    this.pendingAutoRestrictionIDs = [...new Set([...this.pendingAutoRestrictionIDs, ...ids])];
    Promise.resolve().then(() => this.processPendingAutoRestrictions());
  }

  /**
   * Runs pending auto-population only after the required store data has arrived.
   */
  private processPendingAutoRestrictions(): void {
    if (!this.pendingAutoRestrictionIDs.length || !this.resources.length || !this.restrictions.length) {
      return;
    }

    const ids = this.pendingAutoRestrictionIDs;
    this.pendingAutoRestrictionIDs = [];
    ids.forEach(id => this.autoRestriction(id));
  }

  /**
   * Loads form-specific restrictions and retries queued auto-population once restriction data is present.
   *
   * Dispatches a refresh when the restriction store is empty so selecting a Beamline before the
   * global store load completes still populates the Restrictions table.
   */
  loadRestrictions() {
    if (!this.restrictionsRefreshRequested && !this.store.selectSnapshot((state: any) => state.Restrictions?.data ?? [])?.length) {
      // Ensure delayed restriction loading still lets RSF auto-populate newly selected locations.
      this.restrictionsRefreshRequested = true;
      this.store.dispatch(new RestrictionsRefresh());
    }
    this.restrictionsSubs?.unsubscribe();
    this.restrictions$ = this.store.select((state) => state.Restrictions.data);
    this.restrictionsSubs = this.restrictions$.subscribe((data) => {
      this.restrictions = data.filter((x) => x.scf && this.formType == FormType.SCF || x.rsswa && this.formType == FormType.RSSWA || x.ebf && this.formType == FormType.EBF || x.ppstb && this.formType == FormType.PPSTB || x.rsf && this.formType == FormType.RSF);
      let availablerestrictions: Restriction[] = [];
      this.hydrateResources(this.resources);
      let resources = this.resources.filter((x) => this.locationIDs?.includes(x.id));
      resources?.map((r) => {
        switch (r.type) {
          case ResourceType.AcceleratorLocation:
            availablerestrictions = availablerestrictions.concat(
              ...this.restrictions.filter(x => r.parentResources?.map(p => p.parentResourceID).includes(x.resourceID)));
            break;
          case ResourceType.Accelerator:
            availablerestrictions = availablerestrictions.concat(
              ...this.restrictions.filter(
                (x) => x.resourceID == r.id || x.locationID == r.id
              )
            );
            r.parentResources?.map(c => {
              if (
                c.parentResourceID &&
                c.parentResourceType == ResourceType.Accelerator
              ) {
                availablerestrictions = availablerestrictions.concat(
                  ...this.restrictions.filter(
                    (x) =>
                      x.resourceID == c.parentResourceID ||
                      x.locationID == c.parentResourceID
                  )
                );
              }
            });
            break;

          case ResourceType.Beamline:
            availablerestrictions = availablerestrictions.concat(
              ...this.restrictions.filter(
                (x) =>
                  x.resourceID == r.id ||
                  x.locationID == r.id
              )
            );
            r.parentResources?.map((c) => {
              if (c.parentResourceID) {
                availablerestrictions = availablerestrictions.concat(
                  ...this.restrictions.filter(
                    (x) =>
                      x.resourceID == c.parentResourceID ||
                      x.locationID == c.parentResourceID
                  )
                );
              }
            });

            break;

          case ResourceType.Shutter:
            availablerestrictions = availablerestrictions.concat(
              ...this.restrictions.filter(
                (x) =>
                  x.resourceID == r.id ||
                  x.locationID == r.id
              )
            );

            break;

          case ResourceType.Branchline:
            availablerestrictions = availablerestrictions.concat(
              ...this.restrictions.filter(
                (x) => x.resourceID == r.id || x.locationID == r.id
              )
            );
            r.parentResources?.map((x) => {
              if (x.parentResourceID) {
                const parentResource = this.resources.find(
                  (rr) => rr.id == x.parentResourceID
                );
                parentResource?.parentResources?.map((c) => {
                  if (c.parentResourceID) {
                    availablerestrictions = availablerestrictions.concat(
                      ...this.restrictions.filter(
                        (x) =>
                          x.resourceID == c.parentResourceID ||
                          x.locationID == c.parentResourceID
                      )
                    );
                  }
                });
              }
            });
            break;
        }
      });

      this.filteredRestrictions = availablerestrictions.filter((x) => !this.formRestrictions?.map((r) => r.restrictionID).includes(x.id));
      this.uniqueRestrictions = [...new Set(this.filteredRestrictions)];
      this.processPendingAutoRestrictions();
    });
  }

  refresh(resourceIds: number[], selectedRestrictions?: (Restriction | undefined)[]) {
    this.locationIDs = resourceIds;
    this.loadResources();
  }

  onRestrictionInputKeyup(event: KeyboardEvent) {
    if (!event.code?.includes('Arrow')) {
      if (event.keyCode == 13 && this.restrictionInput?.nativeElement.value.trim() != "") {
        this.onFreeTextRestrictionWritten.emit(this.restrictionInput?.nativeElement.value.trim());
        this.clear();
      } else {
        if (this.restrictionInput != null) {
          this.filteredRestrictions = this.availableRestrictions?.filter(x => x?.restrictionLanguage?.toLocaleLowerCase()?.includes(this.restrictionInput?.nativeElement?.value?.toLocaleLowerCase() ?? ''));
        }
      }
    }
  }

  /**
   * Auto-populates restrictions whenever a location is added.
   *
   * Beamlines match direct restrictions plus parent shutter or accelerator restrictions.
   * @param locationID - Resource ID that was newly selected.
   */
  autoRestriction(locationID: number) {
    let autoRestrictions: Restriction[] = [];
    this.hydrateResources(this.resources);
    let resource = this.resources.find(x => x.id == locationID);
    if (resource) {
      switch (resource.type) {
        case this.resourceTypeEnum.AcceleratorLocation:
          autoRestrictions = this.restrictions.filter(x => resource?.parentResources?.map(p => p.parentResourceID).includes(x.resourceID));
          break;
        case this.resourceTypeEnum.Accelerator:
          autoRestrictions = this.restrictions.filter(r => r.resourceID == locationID);
          autoRestrictions = autoRestrictions.concat(this.restrictions.filter(x => resource?.parentResources?.map(p => p.parentResourceID).includes(x.resourceID)));
          break;
        case this.resourceTypeEnum.Beamline:
          autoRestrictions = this.restrictions.filter(x =>
            (x.resourceID == locationID ||
            x.locationID == locationID ||
            resource?.parentResources?.map(p => p.parentResourceID).includes(x.resourceID) ||
            resource?.parentResources?.map(p => p.parentResourceID).includes(x.locationID)) &&
            // Bug 6939: branchline lockouts belong to their branchline; keep them out of the beamline auto-add (still selectable by hand)
            !this.isBranchlineLocation(x.locationID)
          );
          break;
        case this.resourceTypeEnum.Shutter:
          autoRestrictions = this.restrictions.filter(x => resource?.id == x.resourceID || resource?.id == x.locationID);
          break;
        case this.resourceTypeEnum.Branchline:
          autoRestrictions = this.restrictions.filter(r => r.locationID == locationID);
          break;
      }

      if (autoRestrictions) {
        const missingRestrictions = autoRestrictions.filter(a => !this.formRestrictions?.map(r => r.restrictionID).includes(a.id));
        if (missingRestrictions.length) {
          missingRestrictions.forEach(restriction => {
            this.select(restriction);
          });
        }
        else this.locationAddedIDs = [];
      }
    }

  }

  /**
   * Checks whether a restriction is located on a branchline, using the loaded resources catalog.
   * @param locationID - The restriction's location resource ID.
   * @returns True when the location resolves to a Branchline resource.
   */
  private isBranchlineLocation(locationID?: number): boolean {
    return this.resources.find(r => r.id == locationID)?.type == this.resourceTypeEnum.Branchline;
  }

  select(restriction: Restriction) {
    this.clear();
    this.onRestrictionSelected.emit(restriction);
  }

  clear() {
    if (this.restrictionInput)
      this.restrictionInput.nativeElement.value = '';
    this.filteredRestrictions = [];
    this.availableRestrictions?.map(x => {
      this.filteredRestrictions?.push(x);
    });
  }

  reset() {
    this.allRestrictions = [];
    this.clear();
  }

  blur(text: string) {
    if (text.trim() != "") {
      this.onFreeTextRestrictionWritten.emit(text);
      this.clear();
    }
  }

}
